import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, List, Badge, Spin, Empty, notification, Typography, Button, Tag, Tooltip, Row, Col, Divider, Input, Space, Result } from 'antd';
import { MessageOutlined, PhoneOutlined, ClockCircleOutlined, ArrowLeftOutlined, ReloadOutlined, SearchOutlined, DownOutlined, UpOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { userApi } from '../api/apiService';
import { smsApi } from '../api/apiService';
import moment from 'moment-timezone';
import { useNavigate, useParams } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

// Add these styles to the top of the file, after the imports
const messageAnimationStyle = {
  '@keyframes newMessagePulse': {
    '0%': { backgroundColor: '#f0f8ff', transform: 'scale(1)' },
    '50%': { backgroundColor: '#e6f7ff', transform: 'scale(1.02)' },
    '100%': { backgroundColor: 'transparent', transform: 'scale(1)' }
  },
  newMessage: {
    animation: 'newMessagePulse 2s ease-in-out',
    boxShadow: '0 0 10px rgba(24, 144, 255, 0.5)',
    borderLeft: '3px solid #1890ff'
  }
};

// Main Messages component - shows list of accounts as cards
const Messages = () => {
  const { currentUser } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch user accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await userApi.getAccounts();
        
        if (response && response.accounts) {
          setAccounts(response.accounts || []);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching accounts:', error);
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [currentUser]);

  const handleAccountClick = (accountId) => {
    navigate(`/messages/${accountId}`);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '10px' : '20px' }}>
      <Title level={isMobile ? 3 : 2}>Messages</Title>
      
      {accounts.length === 0 ? (
        <Empty description="No accounts found. Add an account to view messages." />
      ) : (
        <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
          {accounts.map(account => (
            <Col xs={24} sm={12} md={8} lg={6} key={account.account_id}>
              <Card 
                hoverable
                onClick={() => handleAccountClick(account.account_id)}
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Tag color={account.platform === 'wephone' ? 'blue' : 'green'}>
                      {account.platform}
                    </Tag>
                    <span style={{ marginLeft: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {account.email || account.phone || account.account_id}
                    </span>
                  </div>
                }
                extra={<MessageOutlined />}
                size={isMobile ? "small" : "default"}
              >
                {!isMobile && (
                  <div>
                    {account.email && <p><strong>Email:</strong> {account.email}</p>}
                    {account.phone && <p><strong>Phone:</strong> {account.phone}</p>}
                    <p>
                      <strong>Subscribed Numbers:</strong> {account.subscribed_numbers?.length || 0}
                    </p>
                  </div>
                )}
                <div style={{ marginTop: isMobile ? 8 : 16, textAlign: 'center' }}>
                  <Button 
                    type="primary" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAccountClick(account.account_id);
                    }}
                    size={isMobile ? "small" : "default"}
                  >
                    View Messages
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

// Account Messages component - shows messages for a specific account
const AccountMessages = () => {
  const { accountId } = useParams();
  const [account, setAccount] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesByNumber, setMessagesByNumber] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newMessages, setNewMessages] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [expandedNumbers, setExpandedNumbers] = useState({});
  const [fetchError, setFetchError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [moreInfoVisible, setMoreInfoVisible] = useState({});
  const navigate = useNavigate();
  const refreshTimerRef = useRef(undefined);
  const previousMessagesRef = useRef([]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch account details
  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        const response = await userApi.getAccounts();
        
        if (response && response.accounts) {
          const foundAccount = response.accounts.find(acc => acc.account_id === accountId);
          if (foundAccount) {
            setAccount(foundAccount);
          } else {
            notification.error({
              message: 'Account not found',
              description: 'The requested account could not be found.'
            });
            navigate('/messages');
          }
        }
      } catch (error) {
        console.error('Error fetching account details:', error);
        notification.error({
          message: 'Error',
          description: 'Failed to load account details.'
        });
      }
    };

    fetchAccountDetails();
  }, [accountId, navigate]);

  // Fetch messages for the account
  const fetchMessages = useCallback(async (isAutoRefresh = false) => {
    if (!account) return;
    
    console.log(`[${new Date().toLocaleTimeString()}] Fetching messages called (auto-refresh: ${isAutoRefresh})`);
    setRefreshing(true);
    
    // Only show loading spinner for manual refreshes or initial load, not auto-refreshes
    if (!isAutoRefresh) {
      setLoading(true);
    }
    
    try {
      console.log('Fetching messages for account:', account);
      
      // Make sure we have the accountId (platform account ID)
      if (!account.accountId) {
        console.error('No platform accountId found in account object:', account);
        notification.error({
          message: 'Error',
          description: 'Platform account ID not found in account data'
        });
        setRefreshing(false);
        setLoading(false);
        return;
      }
      
      // Pass the account object to the API service
      const response = await smsApi.getAccountMessages(account, account.platform);
      
      // Reset error states
      setFetchError(null);
      
      // Check for insufficient balance error
      if (response && response.retcode === 1001) {
        console.log('Insufficient balance detected. Stopping auto-refresh.');
        notification.warning({
          message: 'Insufficient Balance',
          description: response.retmsg || 'Your account has insufficient balance to retrieve messages.',
          duration: 0 // Make the notification persist until manually closed
        });
        
        // Clear the refresh timer to stop auto-refresh
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
        
        setRefreshing(false);
        setLoading(false);
        return;
      }
      
      // Process the messages
      if (response && response.result && response.result.rows) {
        const newMessagesData = response.result.rows;
        
        // Log the complete response from the API
        console.log(`API returned ${newMessagesData.length} total messages (refresh type: ${isAutoRefresh ? 'auto' : 'manual'})`);
        
        // Get current messages from ref for comparison
        const currentMessages = previousMessagesRef.current || []; // Ensure it's an array
        
        // Filter messages with status === 100
        const validMessages = newMessagesData.filter(msg => msg.status === 100);
        console.log(`After filtering for status===100: ${validMessages.length} messages remain`);
        
        // Find truly new messages by comparing IDs
        const currentMessageIds = new Set(currentMessages.map(msg => msg._id));
        console.log(`Current message count: ${currentMessages.length}, IDs: ${Array.from(currentMessageIds).slice(0, 3)}...`);
        
        const actualNewMessages = validMessages.filter(msg => !currentMessageIds.has(msg._id));
        
        console.log(`Found ${actualNewMessages.length} new messages to add (isAutoRefresh: ${isAutoRefresh})`);
        if (actualNewMessages.length > 0) {
          console.log(`New message IDs: ${actualNewMessages.map(msg => msg._id).slice(0, 3)}...`);
          
          // Explicitly log that we're about to trigger subscription
          console.log(`Triggering subscription for ${actualNewMessages.length} new messages during ${isAutoRefresh ? 'auto-refresh' : 'manual operation'}`);
          
          // Add new message IDs to the newMessages array for animation
          setNewMessages(prev => [
            ...prev,
            ...actualNewMessages.map(msg => msg._id)
          ]);
          
          // Play a notification sound if available
          const notificationSound = document.getElementById('notification-sound');
          if (notificationSound) {
            notificationSound.play().catch(e => console.log('Could not play notification sound:', e));
          }
          
          // Merge new messages with existing ones
          const mergedMessages = [...currentMessages, ...actualNewMessages];
          
          // Sort by date (newest first)
          mergedMessages.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA;
          });
          
          // Update state with merged messages
          setMessages(mergedMessages);
          
          // Store the merged messages in the ref for future comparisons
          console.log(`Updating previousMessagesRef from ${previousMessagesRef.current.length} to ${mergedMessages.length} messages`);
          previousMessagesRef.current = mergedMessages;
          
          // Update messagesByNumber with new messages
          const updatedMessagesByNumber = {...messagesByNumber};
          
          // Group messages by phone number, preserving existing messages
          actualNewMessages.forEach(msg => {
            if (!updatedMessagesByNumber[msg.to]) {
              updatedMessagesByNumber[msg.to] = [];
            }
            // Check if this message already exists in the array to avoid duplicates
            const messageExists = updatedMessagesByNumber[msg.to].some(existingMsg => existingMsg._id === msg._id);
            if (!messageExists) {
              updatedMessagesByNumber[msg.to].push(msg);
            }
          });
          
          // Sort messages in each group
          Object.keys(updatedMessagesByNumber).forEach(number => {
            updatedMessagesByNumber[number].sort((a, b) => {
              const dateA = new Date(a.date);
              const dateB = new Date(b.date);
              return dateB - dateA;
            });
          });
          
          // Log the message counts for debugging
          console.log(`Message counts by number after update:`);
          Object.keys(updatedMessagesByNumber).forEach(number => {
            console.log(`${number}: ${updatedMessagesByNumber[number].length} messages`);
          });
          
          setMessagesByNumber(updatedMessagesByNumber);
          
          // Auto-subscribe to numbers from new messages
          console.log(`Processing auto-subscription for ${actualNewMessages.length} new messages...`);
          
          // Create a Set to avoid duplicate subscriptions in the same batch
          const numbersToSubscribe = new Set();
          
          // Extract unique virtual phone numbers (the 'to' field) from the new messages
          actualNewMessages.forEach(msg => {
            if (msg.to && !numbersToSubscribe.has(msg.to)) {
              numbersToSubscribe.add(msg.to);
            }
          });
          
          // Only proceed if there are numbers to subscribe
          if (numbersToSubscribe.size > 0) {
            const numbersArray = Array.from(numbersToSubscribe);
            console.log(`Auto-subscribing to ${numbersArray.length} unique numbers: ${numbersArray.join(', ')}`);
            
            // Call the bulk subscribe API
            userApi.bulkSubscribeNumbers(account.account_id, numbersArray)
              .then(response => {
                console.log('Bulk subscription response:', response);
                
                // Extract statistics from the response
                const stats = response.stats || {
                  already_subscribed: 0,
                  newly_subscribed: 0,
                  failed: 0,
                  total: numbersArray.length
                };
                
                if (stats.failed > 0) {
                  // Only show a small notification for critical errors
                  notification.warning({
                    message: 'Subscription Note',
                    description: `${stats.newly_subscribed} numbers added. ${stats.failed} failed.`,
                    placement: 'bottomRight',
                    duration: 5
                  });
                  
                  // Log detailed results for debugging
                  if (response.results) {
                    const failures = Object.entries(response.results)
                      .filter(([_, status]) => !status.startsWith('success') && !status.startsWith('already'))
                      .map(([number, status]) => ({ number, status }));
                    console.error('Failed subscriptions:', failures);
                  }
                } else if (stats.newly_subscribed > 0) {
                  // Only show a minimal notification for successful new subscriptions
                  notification.info({
                    message: 'Auto-Subscribe',
                    description: `${stats.newly_subscribed} new number${stats.newly_subscribed > 1 ? 's' : ''} subscribed`,
                    placement: 'bottomRight',
                    duration: 3
                  });
                } else {
                  // No notification needed for already subscribed numbers
                  console.log(`All ${stats.already_subscribed} numbers were already subscribed, no action needed.`);
                }
              })
              .catch(error => {
                console.error('Bulk subscription error:', error);
                notification.error({
                  message: 'Subscription Error',
                  description: `Failed to subscribe to phone numbers: ${error.message}`,
                  placement: 'bottomRight',
                  duration: 10
                });
              });
          }
        } else {
          // No new messages, just update the reference for next comparison
          previousMessagesRef.current = validMessages;
          
          // FIXED: Also ensure we update the messages state with valid messages
          // to maintain consistency between the reference and the state
          if (validMessages.length > 0) {
            // Sort by date (newest first)
            validMessages.sort((a, b) => {
              const dateA = new Date(a.date);
              const dateB = new Date(b.date);
              return dateB - dateA;
            });
            
            setMessages(validMessages);
            
            // Update messagesByNumber with all validMessages
            const updatedMessagesByNumber = {...messagesByNumber};
            
            // Group messages by phone number
            validMessages.forEach(msg => {
              if (!updatedMessagesByNumber[msg.to]) {
                updatedMessagesByNumber[msg.to] = [];
              }
              // Check if this message already exists in the array to avoid duplicates
              const messageExists = updatedMessagesByNumber[msg.to].some(existingMsg => existingMsg._id === msg._id);
              if (!messageExists) {
                updatedMessagesByNumber[msg.to].push(msg);
              }
            });
            
            // Sort messages in each group
            Object.keys(updatedMessagesByNumber).forEach(number => {
              updatedMessagesByNumber[number].sort((a, b) => {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return dateB - dateA;
              });
            });
            
            setMessagesByNumber(updatedMessagesByNumber);
          }
        }
      } else {
        console.warn('API response missing expected data structure:', response);
        // Don't clear existing messages if we get an empty response during a refresh
        if (isAutoRefresh) {
          console.log('Preserving existing messages during auto-refresh due to empty API response');
          // Just keep existing state
        } else {
          // FIXED: Don't clear messages on manual refresh either, just keep existing ones
          console.log('Preserving existing messages during manual refresh due to empty API response');
          // Previously: setMessages([]); setMessagesByNumber({});
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      let errorMessage = 'Failed to load messages';
      let errorTitle = 'Error';
      
      // Extract error details from the response if available
      if (error.response) {
        const status = error.response.status;
        const responseData = error.response.data || {};
        
        console.log('Error response details:', {
          status,
          data: responseData,
          message: error.message
        });
        
        // Handle specific error status codes
        if (status === 402) {
          errorTitle = '余额不足';
          errorMessage = '您的账户余额不足，无法获取消息。请联系管理员添加余额。';
          
          // Set fetch error state to display in UI
          setFetchError({
            title: errorTitle,
            message: errorMessage,
            type: 'balance'
          });
        } else {
          errorMessage = responseData.message || responseData.error || error.message || errorMessage;
          
          // Set fetch error state for generic errors
          setFetchError({
            title: `Error (${status})`,
            message: errorMessage,
            type: 'generic'
          });
        }
      } else {
        // Network errors or other exceptions
        setFetchError({
          title: 'Connection Error',
          message: error.message || errorMessage,
          type: 'network'
        });
      }
      
      // Show notification
      notification.error({
        message: errorTitle,
        description: errorMessage,
        duration: 10
      });
      
      // Clear message states
      setMessages([]);
      setMessagesByNumber({});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [account]);

  // Initial fetch when account is loaded
  useEffect(() => {
    if (account) {
      fetchMessages(false); // Pass false for initial load to show loading indicator
    }
  }, [account, fetchMessages]);

  // Set up auto-refresh
  useEffect(() => {
    const setupRefreshTimer = () => {
      // Clear any existing timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      
      // Check if auto-refresh is disabled due to insufficient balance
      if (refreshTimerRef.current === null) {
        console.log('Auto-refresh is disabled due to insufficient balance');
        return;
      }
      
      // Random refresh time between 10-15 seconds
      const refreshTime = Math.floor(Math.random() * 5000) + 10000;
      console.log(`Setting up refresh timer for ${refreshTime/1000} seconds`);
      
      refreshTimerRef.current = setTimeout(() => {
        console.log('Auto-refresh triggered');
        if (account) {
          // Pass true to indicate this is an automatic refresh
          fetchMessages(true);
        }
        setupRefreshTimer();
      }, refreshTime);
    };
    
    if (account) {
      console.log('Initializing refresh timer');
      setupRefreshTimer();
    }
    
    return () => {
      if (refreshTimerRef.current) {
        console.log('Cleaning up refresh timer');
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [account, fetchMessages]);

  // Clear new message indicator after 5 seconds
  useEffect(() => {
    const timers = {};
    
    newMessages.forEach(messageId => {
      const timer = setTimeout(() => {
        setNewMessages(prev => prev.filter(id => id !== messageId));
      }, 5000);
      
      timers[messageId] = timer;
    });
    
    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, [newMessages]);

  // Handle manual refresh
  const handleManualRefresh = () => {
    console.log('Manual refresh triggered by user');
    // Pass false to indicate this is a manual refresh, not an auto-refresh
    fetchMessages(false);
  };

  // Toggle expanded state for a phone number
  const toggleExpanded = (phoneNumber) => {
    setExpandedNumbers(prev => ({
      ...prev,
      [phoneNumber]: !prev[phoneNumber]
    }));
  };

  // Toggle more info for a message on mobile
  const toggleMoreInfo = (messageId) => {
    setMoreInfoVisible(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Filter phone numbers based on search text
  const filteredPhoneNumbers = Object.keys(messagesByNumber).filter(
    number => number.includes(searchText)
  );

  // Inside the AccountMessages component, add a function to format dates
  const formatMessageDate = (dateString) => {
    // Parse the UTC date string
    const utcDate = moment.utc(dateString);
    
    // Convert to local timezone
    const localDate = utcDate.local();
    
    // Format for display
    return {
      formatted: localDate.format('YYYY-MM-DD HH:mm:ss'),
      fromNow: localDate.fromNow(),
      timestamp: localDate.valueOf()
    };
  };

  // Function to mark a message as read (no longer new)
  const markAsRead = (messageId) => {
    setNewMessages(prev => prev.filter(id => id !== messageId));
  };

  // Function to check if a message is recent (less than 24 hours old)
  const isRecent = (dateString) => {
    if (!dateString) return false;
    
    try {
      // Use moment.js for more consistent cross-browser date parsing
      const messageTime = moment(dateString).valueOf();
      const oneDayAgo = moment().subtract(24, 'hours').valueOf();
      return messageTime > oneDayAgo;
    } catch (e) {
      console.error("Error parsing date:", e, dateString);
      return false; // If we can't parse the date, don't include it
    }
  };

  // Update the message display to show only recent messages and group by phone number
  useEffect(() => {
    // Filter out messages older than 24 hours
    const recentMessages = messages.filter(msg => isRecent(msg.date));
    
    if (recentMessages.length !== messages.length) {
      console.log(`Filtered out ${messages.length - recentMessages.length} messages older than 24 hours`);
      setMessages(recentMessages);
    }
  }, [messages]);

  // Render the message list in a column-based layout
  const renderMessageList = () => {
    return (
      <List
        dataSource={filteredPhoneNumbers}
        renderItem={(phoneNumber) => {
          const messagesForNumber = messagesByNumber[phoneNumber] || [];
          const latestMessage = messagesForNumber[0] || {};
          const isNewNumber = messagesForNumber.some(msg => newMessages.includes(msg._id));
          const expanded = expandedNumbers[phoneNumber] || false;
          const dateInfo = latestMessage.date ? formatMessageDate(latestMessage.date) : { fromNow: 'Unknown' };
          
          return (
            <List.Item 
              key={phoneNumber}
              style={{ 
                borderLeft: isNewNumber ? '5px solid #52c41a' : 'none',
                backgroundColor: isNewNumber ? '#f6ffed' : 'white',
                marginBottom: 8,
                borderRadius: 4,
                padding: isMobile ? '8px 12px' : '12px 16px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              onClick={() => toggleExpanded(phoneNumber)}
              onMouseEnter={() => {
                if (isNewNumber) {
                  // Mark all messages for this number as read
                  messagesForNumber.forEach(msg => {
                    if (newMessages.includes(msg._id)) {
                      markAsRead(msg._id);
                    }
                  });
                }
              }}
            >
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: expanded ? 12 : 0 }}>
                  <Space>
                    <PhoneOutlined />
                    <Typography.Text strong style={{ color: isNewNumber ? '#52c41a' : 'inherit' }}>
                      {phoneNumber}
                    </Typography.Text>
                    {isNewNumber && <Tag color="success">New</Tag>}
                  </Space>
                  <Space>
                    <Typography.Text type="secondary">{dateInfo.fromNow}</Typography.Text>
                    {expanded ? <UpOutlined /> : <DownOutlined />}
                  </Space>
                </div>
                
                {!expanded && (
                  <div style={{ marginTop: 8 }}>
                    <Typography.Paragraph ellipsis={{ rows: 1 }} type="secondary">
                      {latestMessage.text || 'No message content'}
                    </Typography.Paragraph>
                  </div>
                )}
                
                {expanded && (
                  <div style={{ marginTop: 8 }}>
                    {messagesForNumber.filter(msg => isRecent(msg.date)).length > 0 ? (
                      <List
                        dataSource={messagesForNumber.filter(msg => isRecent(msg.date))}
                        renderItem={message => {
                          const msgDateInfo = formatMessageDate(message.date);
                          const isNewMsg = newMessages.includes(message._id);
                          const showMoreInfo = moreInfoVisible[message._id] || false;
                          
                          return (
                            <div 
                              key={message._id} 
                              style={{ 
                                padding: isMobile ? '6px 10px' : '8px 12px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: 4,
                                marginBottom: 8,
                                ...isNewMsg ? messageAnimationStyle.newMessage : {}
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <Typography.Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>
                                  From: {message.from}
                                </Typography.Text>
                                <Space>
                                  {isMobile && (
                                    <Button 
                                      type="text" 
                                      icon={<InfoCircleOutlined />} 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleMoreInfo(message._id);
                                      }}
                                      size="small"
                                    />
                                  )}
                                  <Typography.Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                                    {msgDateInfo.fromNow}
                                  </Typography.Text>
                                </Space>
                              </div>
                              <Typography.Paragraph 
                                style={{ 
                                  marginBottom: 4,
                                  fontSize: isMobile ? '13px' : '14px'
                                }}
                              >
                                {message.text}
                              </Typography.Paragraph>
                              
                              {(!isMobile || showMoreInfo) && (
                                <div>
                                  {message.app && <Tag color="blue">{message.app}</Tag>}
                                  {message.vCode && <Tag color="green">{message.vCode}</Tag>}
                                  {message.type && <Tag color="purple">{message.type}</Tag>}
                                </div>
                              )}
                            </div>
                          );
                        }}
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                        <Empty 
                          description={
                            <span>
                              No recent messages found. <br/>
                              Messages older than 24 hours are not displayed.
                            </span>
                          }
                          image={Empty.PRESENTED_IMAGE_SIMPLE} 
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </List.Item>
          );
        }}
      />
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="account-messages-container">
      {/* Add notification sound element */}
      <audio id="notification-sound" preload="auto">
        <source src="/notification.mp3" type="audio/mpeg" />
      </audio>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: 16,
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/messages')}
          style={{ marginRight: 16, marginBottom: isMobile ? 8 : 0 }}
          size={isMobile ? "small" : "default"}
        >
          {isMobile ? "" : "Back to Accounts"}
        </Button>
        <h2 style={{ 
          margin: 0,
          fontSize: isMobile ? '18px' : '24px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: isMobile ? '100%' : '70%'
        }}>
          {account?.platform} - {account?.phone || account?.email} 
        </h2>
      </div>
      
      <div style={{ 
        marginBottom: 16, 
        display: 'flex', 
        justifyContent: 'space-between',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search phone numbers"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ 
            maxWidth: isMobile ? '100%' : 300,
            marginBottom: isMobile ? 8 : 0
          }}
          size={isMobile ? "small" : "default"}
        />
        <div>
          {process.env.NODE_ENV === 'development' && !isMobile && (
            <Button 
              danger
              onClick={() => {
                // Simulate insufficient balance error
                setFetchError({
                  title: '余额不足',
                  message: '您的账户余额不足，无法获取消息。请联系管理员添加余额。',
                  type: 'balance'
                });
                
                // Show notification
                notification.error({
                  message: '余额不足',
                  description: '您的账户余额不足，无法获取消息。请联系管理员添加余额。',
                  duration: 10
                });
                
                // Clear message states
                setMessages([]);
                setMessagesByNumber({});
              }}
              style={{ marginRight: 8 }}
              size={isMobile ? "small" : "default"}
            >
              测试错误
            </Button>
          )}
          <Button 
            type="primary" 
            icon={<ReloadOutlined spin={refreshing} />} 
            onClick={handleManualRefresh}
            loading={refreshing}
            disabled={refreshing}
            size={isMobile ? "small" : "default"}
          >
            {isMobile ? "" : (refreshing ? 'Refreshing...' : 'Refresh')}
          </Button>
        </div>
      </div>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : fetchError ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Result
            status={fetchError.type === 'balance' ? 'warning' : 'error'}
            title={fetchError.title}
            subTitle={fetchError.message}
            extra={
              fetchError.type === 'balance' ? (
                <div>
                  <Button type="primary" onClick={() => navigate('/profile')} style={{ marginRight: 12 }}>
                    联系管理员
                  </Button>
                  <Button onClick={handleManualRefresh}>
                    重试
                  </Button>
                </div>
              ) : (
                <Button type="primary" onClick={handleManualRefresh}>
                  重试
                </Button>
              )
            }
          />
        </div>
      ) : filteredPhoneNumbers.length === 0 ? (
        <Empty description="No messages found" />
      ) : (
        renderMessageList()
      )}
    </div>
  );
};

// Export both components
export { Messages as default, AccountMessages }; 